// Dire quand a lieu un événement, en français et en clair.
//
// « 09/08/2026 14:00 » ne parle à personne. « Demain à 14 h » se lit d'un coup
// d'œil, et c'est ce qui décide quelqu'un à venir ou non. Au-delà d'une semaine,
// la date complète reprend son sens — « dans 23 jours » ne se situe plus.

const JOURS = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
const MOIS = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

const memeJour = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** « 14 h » ou « 14 h 30 » — jamais « 14:00 », qu'on ne dit pas à l'oral. */
export function heureLisible(ts) {
  const d = new Date(ts);
  const m = d.getMinutes();
  return m ? `${d.getHours()} h ${String(m).padStart(2, "0")}` : `${d.getHours()} h`;
}

/** « Aujourd'hui à 18 h », « Samedi 15 août à 9 h », « Il y a 3 jours ». */
export function quandLisible(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const maintenant = new Date();
  const jourEnMs = 864e5;

  if (memeJour(d, maintenant)) return `Aujourd'hui à ${heureLisible(ts)}`;

  const demain = new Date(maintenant.getTime() + jourEnMs);
  if (memeJour(d, demain)) return `Demain à ${heureLisible(ts)}`;

  const hier = new Date(maintenant.getTime() - jourEnMs);
  if (memeJour(d, hier)) return `Hier à ${heureLisible(ts)}`;

  const ecart = Math.round((d - maintenant) / jourEnMs);
  // Dans la semaine qui vient, le nom du jour suffit et se situe tout seul.
  if (ecart > 0 && ecart < 7) return `${JOURS[d.getDay()]} à ${heureLisible(ts)}`;

  const annee = d.getFullYear() !== maintenant.getFullYear() ? ` ${d.getFullYear()}` : "";
  const date = `${JOURS[d.getDay()]} ${d.getDate()} ${MOIS[d.getMonth()]}${annee}`;
  return `${date.charAt(0).toUpperCase()}${date.slice(1)} à ${heureLisible(ts)}`;
}

/** Un événement est « passé » une fois la journée terminée, pas à l'heure pile :
 *  un culte de 9 h intéresse encore à 11 h. */
export const estPasse = (ts) => {
  const fin = new Date(ts);
  fin.setHours(23, 59, 59, 999);
  return fin.getTime() < Date.now();
};

/**
 * Un direct est « en cours » un quart d'heure avant l'heure annoncée et pendant
 * quatre heures après. Personne ne pense à venir appuyer sur un bouton au début
 * ni à la fin d'un culte : mieux vaut une fenêtre large et automatique qu'un
 * bandeau rouge qu'on oublie d'éteindre.
 */
export function enDirect(e) {
  if (!e?.direct || !e?.lienDirect) return false;
  const debut = e.quand - 15 * 60 * 1000;
  const fin = e.quand + 4 * 3600 * 1000;
  const maintenant = Date.now();
  return maintenant >= debut && maintenant <= fin;
}

/**
 * L'adresse d'un lecteur intégrable, à partir d'un lien collé tel quel.
 * Rend `null` si l'on ne reconnaît pas — on proposera alors d'ouvrir le lien.
 */
export function lecteurIntegrable(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return `https://www.youtube-nocookie.com/embed/${u.pathname.slice(1)}`;
    if (u.hostname.includes("youtube.com")) {
      const v = u.searchParams.get("v");
      if (v) return `https://www.youtube-nocookie.com/embed/${v}`;
      if (u.pathname.startsWith("/live/")) return `https://www.youtube-nocookie.com/embed/${u.pathname.split("/")[2]}`;
    }
    if (u.hostname.includes("facebook.com"))
      return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false`;
  } catch {
    return null;
  }
  return null;
}
